import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
  Button,
  Grid,
  Icon,
  Input,
  Popup,
  Card,
  Modal,
  Dropdown,
} from "semantic-ui-react";
import styled from 'styled-components';
import axios from "axios";
import { buildApiUrl, buildMapsUrl } from "../utils/apiConfig";
import { countMapExportArtifacts, getAdditionalJpgArtifacts } from "../utils/mapExportArtifacts";
import { replaceBrTags } from "../utils/stringUtils";
import { extractInviteToken, isUuid, toLegacyMapFeatureFromLite } from "../utils/mapVersioning";
import { useMapContext } from "./MapContext";
import { useAuth } from "./authContext";
import SidebarWrapper from './SidebarWrapper';

// Styled components for MapListBar
const StyledGridRow = styled(Grid.Row)`
  padding: ${({ theme }) => theme.spacing[2]} 0;
`;

const ButtonGroupContainer = styled(Button.Group)`
  margin-bottom: ${({ theme }) => theme.spacing[0.5]};
  margin-left: ${({ theme }) => theme.spacing[4]};
  margin-right: ${({ theme }) => theme.spacing[4]};
`;

const SearchInput = styled(Input)`
  width: 100%;
  margin-bottom: ${({ theme }) => theme.spacing[0.5]};
`;

const CardGroupContainer = styled(Card.Group)`
  overflow: auto;
  max-height: ${({ $isMobile }) => $isMobile ? '30vh' : '80vh'};
  overflow-x: hidden;
  width: 100%;
`;

const StyledCard = styled(Card)`
  max-height: 80px;
  margin: ${({ theme }) => theme.spacing[3]};
  width: 90%;
  position: relative;
`;

const CardButtonGrid = styled.div`
  float: right;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing[1]};
  width: 90px;
`;

const CardButton = styled(Button)`
  padding: ${({ theme }) => theme.spacing[2]};
  margin: 0;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: ${({ theme }) => theme.colors.neutral[0]}cc;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
`;

export default function MapListBar({
  activeUser,
  userMaps,
  setUserMaps,
  corporateMaps,
  setCorporateMaps = () => {},
  setActiveMapToEdit,
  getApiHeaders,
  setShowSideListBar,
  isMobile,
  activeMapToEdit, // Add this to know when edit bar is open
  sidebarConfig, // Add sidebar configuration
  fetchFullMapData, // Add function to fetch full map data
  onHeightChange, // Add callback for height changes
}) {
  const {
    isMapReady,
    clearLayer,
    addMappoints,
    fitToMarkerSource,
    onMarkerClick,
  } = useMapContext();
  const { authenticatedUser, isImpersonating } = useAuth();

  const [searchQuery, setSearchQuery] = useState("");
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [regeneratingMaps, setRegeneratingMaps] = useState(new Set());
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedMapForQr, setSelectedMapForQr] = useState(null);
  const [isClaimingInvite, setIsClaimingInvite] = useState(false);
  const [claimError, setClaimError] = useState("");
  const [selectedOwnerTags, setSelectedOwnerTags] = useState([]);
  const [selectedMyTags, setSelectedMyTags] = useState([]);
  const [showBulkRegenerationControls, setShowBulkRegenerationControls] = useState(false);
  const [bulkRegenerationScope, setBulkRegenerationScope] = useState("filtered");
  const [bulkRegenerationOwnership, setBulkRegenerationOwnership] = useState("all");
  const [bulkRegenerationType, setBulkRegenerationType] = useState("all");
  const [selectedBulkRegenerationMapIds, setSelectedBulkRegenerationMapIds] = useState([]);
  const [currentRegeneratingMapName, setCurrentRegeneratingMapName] = useState("");
  const [stopRegenerationRequested, setStopRegenerationRequested] = useState(false);
  const [bulkRegenerationJobId, setBulkRegenerationJobId] = useState(null);
  const [bulkRegenerationStatus, setBulkRegenerationStatus] = useState(null);
  const autoClaimAttemptedRef = useRef(false);
  const stopRegenerationRequestedRef = useRef(false);
  const bulkRegenerationPollRef = useRef(null);

  const normalizeTags = useCallback((values) => {
    if (!Array.isArray(values)) return [];
    const seen = new Set();
    const normalized = [];
    values.forEach((value) => {
      const tag = String(value || "").trim().toLowerCase();
      if (!tag || seen.has(tag)) return;
      seen.add(tag);
      normalized.push(tag);
    });
    return normalized;
  }, []);

  const mapsToShow = useMemo(() => {
    const merged = [...(userMaps || []), ...(corporateMaps || [])];
    const seen = new Set();
    return merged.filter((item) => {
      const key = String(item?.id || "");
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [userMaps, corporateMaps]);
  const isVersionedMap = useCallback((mapItem) => {
    if (!mapItem) return false;
    if (mapItem.is_versioned) return true;
    return isUuid(String(mapItem.id || ""));
  }, []);
  const canManageTierCapabilities = Boolean(authenticatedUser?.is_staff) && !isImpersonating;
  const bulkRegenerationStorageKey = useMemo(
    () => `qrmaps.bulkMapExportJob.${String(authenticatedUser?.id || "anonymous")}`,
    [authenticatedUser?.id]
  );

  const closeMapListBar = () => {
    setShowSideListBar(false);
    clearLayer("markers");
  };

  const toVersionedMapRootHint = useCallback((mapItem) => {
    if (!mapItem || !isVersionedMap(mapItem)) return null;
    return {
      id: mapItem.id,
      name: mapItem.properties?.area_name || mapItem.name || "",
      owner_id: mapItem.properties?.map_user ?? mapItem.owner_id ?? null,
      owner_tags: Array.isArray(mapItem.properties?.owner_tags) ? mapItem.properties.owner_tags : [],
      my_tags: Array.isArray(mapItem.properties?.my_tags) ? mapItem.properties.my_tags : [],
      can_edit: Boolean(mapItem.can_edit),
      current_revision_id: mapItem.current_revision_id || null,
    };
  }, [isVersionedMap]);

  const addMap = () => {
    setActiveMapToEdit({
      geometry: null,
      id: null,
      is_new: true,
      is_versioned: true,
      can_edit: true,
      current_revision_id: null,
      properties: {
        api_key: null,
        area_name: "",
        country_name: "",
        mappoints: [],
        map_user: activeUser?.object_id ?? null,
        visibility_settings: {},
        owner_tags: [],
        my_tags: [],
      },
      type: "Feature",
    });
  };

  const editMap = useCallback(async (id) => {
    // First try to find in merged maps (might be lightweight data)
    const existingMap = mapsToShow.find((m) => String(m.id) === String(id));

    if (existingMap?.has_full_details) {
      // Already have full data, use it
      console.log("mapToEdit (from cache)", existingMap);
      setActiveMapToEdit(existingMap);
    } else {
      // Need to fetch full map data
      console.log("Fetching full map data for editing...");
      const fullMapData = await fetchFullMapData(id, toVersionedMapRootHint(existingMap));
      if (fullMapData) {
        console.log("mapToEdit (fetched)", fullMapData);
        setActiveMapToEdit(fullMapData);
      }
    }
  }, [mapsToShow, fetchFullMapData, setActiveMapToEdit, toVersionedMapRootHint]);

  const duplicateCorporateMap = async (mapToDuplicate) => {
    try {
      if (!mapToDuplicate) return;
      
      // Check if we have full data or need to fetch it
      let fullMapData = mapToDuplicate;
      if (!mapToDuplicate?.has_full_details) {
        console.log("Fetching full map data for duplication...");
        fullMapData = await fetchFullMapData(mapToDuplicate.id, toVersionedMapRootHint(mapToDuplicate));
        if (!fullMapData) {
          console.error("Failed to fetch full map data for duplication");
          return;
        }
      }
      
      if (isVersionedMap(fullMapData)) {
        setActiveMapToEdit({
          ...fullMapData,
          is_new: false,
        });
        return;
      }

      const copy = {
        ...fullMapData,
        id: null,
        properties: {
          ...fullMapData.properties,
          api_key: null,
          map_user: activeUser?.object_id,
          area_name: `${fullMapData.properties.area_name} (Copy)`,
          mappoints: (fullMapData.properties.mappoints || []).map((pt) => ({
            ...pt,
            id: null,
            osm_id: pt.osm_id,
            name: pt.name,
            number: pt.number,
            center: pt.center,
          })),
        },
        is_new: true,
        is_corporate: false,
      };
      delete copy.is_corporate;
      setActiveMapToEdit(copy);
    } catch (err) {
      console.log(err);
    }
  };

  const openQrModal = useCallback(async (map) => {
    if (!map) return;

    let mapForModal = map;
    if (!map?.has_full_details) {
      const fullMapData = await fetchFullMapData(map.id, toVersionedMapRootHint(map));
      if (fullMapData) {
        mapForModal = fullMapData;
      }
    }

    setSelectedMapForQr(mapForModal);
    setQrModalOpen(true);
  }, [fetchFullMapData, toVersionedMapRootHint]);

  const closeQrModal = useCallback(() => {
    setQrModalOpen(false);
    setSelectedMapForQr(null);
  }, []);

  const renderDownloadCard = useCallback(({
    href,
    icon,
    label,
    borderColor,
    hoverColor,
    badgeIcon = null,
  }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textDecoration: "none",
        color: "inherit",
        padding: "15px",
        border: `2px solid ${borderColor}`,
        borderRadius: "8px",
        backgroundColor: "#f8f9fa",
        width: "160px",
        transition: "all 0.2s",
        cursor: "pointer"
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = hoverColor;
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = "0 4px 8px rgba(0,0,0,0.1)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = "#f8f9fa";
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      <div style={{ fontSize: "40px", marginBottom: "8px", position: "relative" }}>
        <Icon name={icon} style={{ color: borderColor }} />
        {badgeIcon ? (
          <Icon
            name={badgeIcon}
            style={{
              position: "absolute",
              bottom: "-5px",
              right: "-5px",
              fontSize: "18px",
              color: "#2185d0",
              backgroundColor: "white",
              borderRadius: "3px",
              padding: "2px"
            }}
          />
        ) : null}
      </div>
      <span style={{
        fontSize: "13px",
        fontWeight: "600",
        textAlign: "center",
        color: "#333"
      }}>
        {label}
      </span>
    </a>
  ), []);

  const removeMapFromLocalLists = useCallback((id) => {
    const idText = String(id);
    setUserMaps((prev) => (prev || []).filter((m) => String(m.id) !== idText));
    setCorporateMaps((prev) => (prev || []).filter((m) => String(m.id) !== idText));
    setSelectedBulkRegenerationMapIds((prev) => (prev || []).filter((mapId) => String(mapId) !== idText));
    if (String(activeMapToEdit?.id || "") === idText) {
      setActiveMapToEdit(null);
    }
  }, [activeMapToEdit?.id, setActiveMapToEdit, setCorporateMaps, setUserMaps]);

  const deleteMap = useCallback(async (id) => {
    const mapToDelete = mapsToShow.find((m) => String(m.id) === String(id));
    if (!mapToDelete) return;

    const isOwned = String(mapToDelete?.properties?.map_user) === String(activeUser?.object_id);
    const actionLabel = isOwned ? "delete this map" : "remove this map from your list";
    const confirmed = window.confirm(`Are you sure you want to ${actionLabel}?`);
    if (!confirmed) return;

    const isNew = mapToDelete?.is_new;
    if (isNew) {
      removeMapFromLocalLists(id);
      return;
    }

    const headers = await getApiHeaders();
    const deleteVersionedMap = async (detachFromMyTours = false) => axios({
      url: buildApiUrl(`maps/${id}/`),
      method: "delete",
      headers,
      data: { detach_from_my_tours: detachFromMyTours },
    });

    try {
      if (isVersionedMap(mapToDelete)) {
        await deleteVersionedMap(false);
      } else {
        await axios({
          url: buildApiUrl(`map/${id}/`),
          method: "delete",
          headers,
        });
      }
      removeMapFromLocalLists(id);
    } catch (err) {
      if (isVersionedMap(mapToDelete) && err?.response?.status === 409 && err.response?.data?.reason === "map_used_by_tours") {
        const tourCount = err.response.data.tour_count || 0;
        const detachConfirmed = window.confirm(
          `This map is attached to ${tourCount} of your tour${tourCount === 1 ? "" : "s"}. Detach it from those tours and continue?`
        );
        if (!detachConfirmed) return;
        try {
          await deleteVersionedMap(true);
          removeMapFromLocalLists(id);
        } catch (retryErr) {
          console.error("Failed to delete map after detaching tours:", retryErr);
          alert(retryErr?.response?.data?.detail || "Failed to delete map.");
        }
        return;
      }
      console.error("Failed to delete map:", err);
      alert(err?.response?.data?.detail || "Failed to delete map.");
    }
  }, [
    activeUser?.object_id,
    getApiHeaders,
    isVersionedMap,
    mapsToShow,
    removeMapFromLocalLists,
  ]);

  const refreshVersionedMaps = useCallback(async () => {
    const headers = await getApiHeaders();
    const mapsResponse = await axios({
      url: buildApiUrl("maps_lite/"),
      method: "get",
      headers,
    });
    const mapRoots = Array.isArray(mapsResponse.data) ? mapsResponse.data : [];
    const normalizedMaps = mapRoots
      .map((mapRoot) => toLegacyMapFeatureFromLite({ mapRoot }))
      .filter(Boolean);

    const ownedMaps = normalizedMaps
      .filter((mapItem) => String(mapItem?.properties?.map_user) === String(activeUser?.object_id))
      .map((mapItem) => ({ ...mapItem, is_corporate: false }));
    const sharedMaps = normalizedMaps
      .filter((mapItem) => String(mapItem?.properties?.map_user) !== String(activeUser?.object_id))
      .map((mapItem) => ({ ...mapItem, is_corporate: true }));

    setUserMaps(ownedMaps);
    setCorporateMaps(sharedMaps);
  }, [activeUser?.object_id, getApiHeaders, setCorporateMaps, setUserMaps]);

  const claimInvite = useCallback(async (rawToken) => {
    const inviteToken = extractInviteToken(rawToken);
    if (!inviteToken) {
      setClaimError("Invite token is empty.");
      return;
    }

    try {
      setClaimError("");
      setIsClaimingInvite(true);
      await axios.post(
        buildApiUrl("maps/invite-links/claim/"),
        { invite_token: inviteToken },
        { headers: await getApiHeaders() },
      );
      try {
        await refreshVersionedMaps();
      } catch (refreshError) {
        console.error("Invite claimed but map refresh failed:", refreshError);
      }
      alert("Map invite claimed successfully.");
    } catch (error) {
      const message = error?.response?.data?.detail || "Failed to claim invite link.";
      setClaimError(message);
      alert(message);
    } finally {
      setIsClaimingInvite(false);
    }
  }, [getApiHeaders, refreshVersionedMaps]);

  const handleJoinMap = useCallback(() => {
    const input = window.prompt("Paste invite link or invite token:");
    if (!input) return;
    claimInvite(input);
  }, [claimInvite]);

  const replaceMapInLists = useCallback((nextMap) => {
    if (!nextMap?.id) return;
    const mapId = String(nextMap.id);
    const replace = (list) => {
      if (!Array.isArray(list)) return list;
      const index = list.findIndex((item) => String(item?.id) === mapId);
      if (index === -1) return list;
      const copy = list.slice();
      copy[index] = nextMap;
      return copy;
    };
    setUserMaps((prev) => replace(prev));
    setCorporateMaps((prev) => replace(prev));
  }, [setCorporateMaps, setUserMaps]);

  useEffect(() => () => {
    if (bulkRegenerationPollRef.current) {
      clearInterval(bulkRegenerationPollRef.current);
      bulkRegenerationPollRef.current = null;
    }
  }, []);

  // —— Drop one OL marker per map (centered) when the list/tab changes ——
  // Only show markers when MapListBar is actually visible (not when MapEditBar is open)
  useEffect(() => {
    if (!isMapReady()) return;
    
    // Don't show map markers when MapEditBar is open (activeMapToEdit is set)
    // This prevents the effect from running when MapEditBar is open and userMaps changes
    if (activeMapToEdit) {
      return;
    }
    
    console.log('mapsToShow', mapsToShow);
    // Build a mappoint per map (GeoJSON Point in EPSG:4326)
    const toMappoints = mapsToShow
      .map((m) => {
        // For map_lite data, we need to handle the new structure
        // map_lite returns maps with center_r field instead of geometry
        if (m?.geometry && Array.isArray(m.geometry)) {
          // center_r is [lng, lat] format
          return {
            id: `map-${m.id}`,
            number: undefined,
            center: { type: "Point", coordinates: m.geometry },
            name: m?.properties?.area_name,
          };
        }

        // Fallback: prefer explicit geometry if it's already a Point
        const g = m?.geometry;
        if (g?.type === "Point" && Array.isArray(g.coordinates)) {
          return {
            id: `map-${m.id}`,          // keep IDs unique, won't clash with mp-<id> from tours
            number: undefined,          // omit to render as plain dot in your style
            center: g,                  // GeoJSON Point {type, coordinates:[lon,lat]}
            name: m?.area_name || m?.properties?.area_name,
          };
        }

        // Otherwise compute a center from NE/SW (both are [lat, lon])
        const ne = m?.properties?.ne_r;
        const sw = m?.properties?.sw_r;
        if (Array.isArray(ne) && Array.isArray(sw)) {
          const lat = (ne[0] + sw[0]) / 2;
          const lon = (ne[1] + sw[1]) / 2;
          return {
            id: `map-${m.id}`,
            number: undefined,
            center: { type: "Point", coordinates: [lon, lat] },
            name: m?.properties?.area_name,
          };
        }

        // Lastly, fall back to first mappoint if present
        const first = m?.properties?.mappoints?.[0]?.center;
        if (first?.type === "Point" && Array.isArray(first.coordinates)) {
          return {
            id: `map-${m.id}`,
            number: undefined,
            center: first,
            name: m?.properties?.area_name,
          };
        }
        return null;
      })
      .filter(Boolean)
      .map((point) => ({
        ...point,
        isMapListMarker: true,
        isMapMarker: true,
        disableMarkerDeclutter: true,
      }));

    // Update OL markers layer
    clearLayer("markers");
    if (toMappoints.length) {
      addMappoints(toMappoints);
      // Automatically focus to the area of the markers
      fitToMarkerSource({ padding: [50, 50, 50, 50] });
    }

  }, [isMapReady, mapsToShow, clearLayer, addMappoints, fitToMarkerSource, activeMapToEdit]);

  // —— Handle marker clicks ——
  useEffect(() => {
    if (!onMarkerClick) return;
    if (activeMapToEdit) return; // Don't handle clicks when MapEditBar is open

    const unsubscribe = onMarkerClick((markerId, feature) => {
      console.log('MapListBar: marker clicked, markerId:', markerId);
      
      if (!markerId || !feature) {
        console.log('MapListBar: no markerId or feature');
        return;
      }
      
      // Extract map ID from marker ID (format: mp-map-123 -> 123)
      const mapId = String(markerId).replace('mp-map-', '');
      console.log('MapListBar: extracted mapId:', mapId);

      if (!mapId) {
        console.log('MapListBar: mapId is empty');
        return;
      }
      
      // Find the map in the current list
      const clickedMap = mapsToShow.find(m => String(m.id) === String(mapId));
      console.log('MapListBar: clickedMap found:', clickedMap?.properties?.area_name);
      
      if (!clickedMap) {
        console.log('MapListBar: no clickedMap found in mapsToShow');
        return;
      }
      
      if (clickedMap?.can_edit) {
        // Editable map -> open editor
        console.log('MapListBar: calling editMap for user map');
        editMap(mapId);
      } else {
        // Read-only map -> open QR/info modal
        console.log('MapListBar: calling openQrModal for corporate map');
        openQrModal(clickedMap);
      }
    });

    return unsubscribe;
  }, [onMarkerClick, mapsToShow, activeMapToEdit, editMap, openQrModal]);

  // —— Cleanup Registration ——
  const mapCtx = useMapContext();
  useEffect(() => {
    if (!mapCtx?.registerSidebarCleanup) return;

    const cleanup = () => {
      console.log('MapListBar: cleaning up map objects');
      clearLayer('markers');
    };

    mapCtx.registerSidebarCleanup('mapList', cleanup);

    return () => {
      mapCtx.registerSidebarCleanup('mapList', null);
    };
  }, [mapCtx, clearLayer]);

  useEffect(() => {
    if (autoClaimAttemptedRef.current) return;
    autoClaimAttemptedRef.current = true;
    const params = new URLSearchParams(window.location.search);
    const inviteToken = params.get("map_invite_token");
    if (!inviteToken) return;

    claimInvite(inviteToken);
    params.delete("map_invite_token");
    const nextQuery = params.toString();
    const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ""}${window.location.hash || ""}`;
    window.history.replaceState({}, "", nextUrl);
  }, [claimInvite]);

  const ownerTagOptions = useMemo(() => {
    const tags = new Set();
    mapsToShow.forEach((mapItem) => {
      const ownerTags = mapItem?.properties?.owner_tags || [];
      ownerTags.forEach((tag) => {
        const normalized = String(tag || "").trim().toLowerCase();
        if (normalized) tags.add(normalized);
      });
    });
    return Array.from(tags)
      .sort((a, b) => a.localeCompare(b))
      .map((tag) => ({ key: `owner-${tag}`, text: tag, value: tag }));
  }, [mapsToShow]);

  const myTagOptions = useMemo(() => {
    const tags = new Set();
    mapsToShow.forEach((mapItem) => {
      const userTags = mapItem?.properties?.my_tags || [];
      userTags.forEach((tag) => {
        const normalized = String(tag || "").trim().toLowerCase();
        if (normalized) tags.add(normalized);
      });
    });
    return Array.from(tags)
      .sort((a, b) => a.localeCompare(b))
      .map((tag) => ({ key: `my-${tag}`, text: tag, value: tag }));
  }, [mapsToShow]);

  const filteredMaps = useMemo(() => {
    return mapsToShow
      .filter((m) => {
        const matchesText =
          (m?.properties?.area_name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
          (m?.properties?.country_name || "").toLowerCase().includes(searchQuery.toLowerCase());

        const ownerTags = normalizeTags(m?.properties?.owner_tags || []);
        const myTags = normalizeTags(m?.properties?.my_tags || []);
        const matchesOwnerTags = selectedOwnerTags.length === 0
          || selectedOwnerTags.every((tag) => ownerTags.includes(tag));
        const matchesMyTags = selectedMyTags.length === 0
          || selectedMyTags.every((tag) => myTags.includes(tag));

        return matchesText && matchesOwnerTags && matchesMyTags;
      })
      .sort((a, b) =>
        (a?.properties?.area_name || "").localeCompare(
          b?.properties?.area_name || ""
        )
      );
  }, [mapsToShow, normalizeTags, searchQuery, selectedMyTags, selectedOwnerTags]);

  const bulkRegenerationCandidates = useMemo(() => {
    const baseList = bulkRegenerationScope === "all" ? mapsToShow : filteredMaps;
    return baseList.filter((mapItem) => {
      const isOwned = String(mapItem?.properties?.map_user) === String(activeUser?.object_id);
      const ownershipMatch =
        bulkRegenerationOwnership === "all"
          || (bulkRegenerationOwnership === "owned" && isOwned)
          || (bulkRegenerationOwnership === "shared" && !isOwned);
      const typeMatch =
        bulkRegenerationType === "all"
          || (bulkRegenerationType === "legacy" && !isVersionedMap(mapItem))
          || (bulkRegenerationType === "versioned" && isVersionedMap(mapItem));
      return ownershipMatch && typeMatch;
    });
  }, [
    activeUser?.object_id,
    bulkRegenerationOwnership,
    bulkRegenerationScope,
    bulkRegenerationType,
    filteredMaps,
    isVersionedMap,
    mapsToShow,
  ]);

  const bulkRegenerationCandidateIdSet = useMemo(() => {
    return new Set(bulkRegenerationCandidates.map((mapItem) => String(mapItem?.id)));
  }, [bulkRegenerationCandidates]);

  const manuallySelectedBulkRegenerationCandidates = useMemo(() => {
    if (selectedBulkRegenerationMapIds.length === 0) {
      return [];
    }
    const selectedSet = new Set(selectedBulkRegenerationMapIds.map((id) => String(id)));
    return bulkRegenerationCandidates.filter((mapItem) => selectedSet.has(String(mapItem?.id)));
  }, [bulkRegenerationCandidates, selectedBulkRegenerationMapIds]);

  const effectiveBulkRegenerationCandidates = useMemo(() => {
    if (manuallySelectedBulkRegenerationCandidates.length > 0) {
      return manuallySelectedBulkRegenerationCandidates;
    }
    return bulkRegenerationCandidates;
  }, [bulkRegenerationCandidates, manuallySelectedBulkRegenerationCandidates]);

  useEffect(() => {
    if (!showBulkRegenerationControls) {
      setSelectedBulkRegenerationMapIds([]);
      return;
    }
    setSelectedBulkRegenerationMapIds((prev) =>
      prev.filter((mapId) => bulkRegenerationCandidateIdSet.has(String(mapId)))
    );
  }, [bulkRegenerationCandidateIdSet, showBulkRegenerationControls]);

  const bulkRegenerationScopeOptions = useMemo(() => ([
    { key: "filtered", text: `Filtered maps (${filteredMaps.length})`, value: "filtered" },
    { key: "all", text: `All visible maps (${mapsToShow.length})`, value: "all" },
  ]), [filteredMaps.length, mapsToShow.length]);

  const bulkRegenerationOwnershipOptions = useMemo(() => ([
    { key: "all", text: "All owners", value: "all" },
    { key: "owned", text: "Owned only", value: "owned" },
    { key: "shared", text: "Shared only", value: "shared" },
  ]), []);

  const bulkRegenerationTypeOptions = useMemo(() => ([
    { key: "all", text: "Legacy + versioned", value: "all" },
    { key: "legacy", text: "Legacy only", value: "legacy" },
    { key: "versioned", text: "Versioned only", value: "versioned" },
  ]), []);

  const toggleBulkRegenerationSelection = useCallback((mapId) => {
    if (!showBulkRegenerationControls || isRegenerating) return;
    if (!bulkRegenerationCandidateIdSet.has(String(mapId))) return;
    setSelectedBulkRegenerationMapIds((prev) => {
      const nextId = String(mapId);
      return prev.some((existingId) => String(existingId) === nextId)
        ? prev.filter((existingId) => String(existingId) !== nextId)
        : [...prev, nextId];
    });
  }, [bulkRegenerationCandidateIdSet, isRegenerating, showBulkRegenerationControls]);

  const clearBulkRegenerationSelection = useCallback(() => {
    setSelectedBulkRegenerationMapIds([]);
  }, []);

  const refreshMapsAfterBulkJob = useCallback(async (mapIds) => {
    const uniqueMapIds = Array.from(new Set((mapIds || []).map((mapId) => String(mapId)).filter(Boolean)));
    await Promise.all(uniqueMapIds.map(async (mapId) => {
      try {
        const refreshedMap = await fetchFullMapData(mapId);
        if (refreshedMap) {
          replaceMapInLists(refreshedMap);
        }
      } catch (refreshError) {
        console.error(`Failed to refresh map ${mapId} after bulk export:`, refreshError);
      }
    }));
  }, [fetchFullMapData, replaceMapInLists]);

  const stopBulkRegenerationPolling = useCallback(() => {
    if (bulkRegenerationPollRef.current) {
      clearInterval(bulkRegenerationPollRef.current);
      bulkRegenerationPollRef.current = null;
    }
  }, []);

  const persistBulkRegenerationJob = useCallback((jobId, status = null) => {
    if (typeof window === "undefined") return;
    if (!jobId) return;
    window.localStorage.setItem(
      bulkRegenerationStorageKey,
      JSON.stringify({
        jobId: String(jobId),
        status,
      })
    );
  }, [bulkRegenerationStorageKey]);

  const clearPersistedBulkRegenerationJob = useCallback(() => {
    if (typeof window === "undefined") return;
    window.localStorage.removeItem(bulkRegenerationStorageKey);
  }, [bulkRegenerationStorageKey]);

  const loadPersistedBulkRegenerationJob = useCallback(() => {
    if (typeof window === "undefined") return null;
    const rawValue = window.localStorage.getItem(bulkRegenerationStorageKey);
    if (!rawValue) return null;
    try {
      const parsed = JSON.parse(rawValue);
      if (parsed && parsed.jobId) {
        return {
          jobId: String(parsed.jobId),
          status: parsed.status || null,
        };
      }
    } catch (error) {
      return {
        jobId: String(rawValue),
        status: null,
      };
    }
    return null;
  }, [bulkRegenerationStorageKey]);

  const pollBulkRegenerationJob = useCallback((jobId, mapIds) => {
    stopBulkRegenerationPolling();

    const pollOnce = async () => {
      try {
        const headers = await getApiHeaders();
        const statusResponse = await axios.get(
          buildApiUrl(`bulk_map_export_job/${jobId}/status/`),
          { headers }
        );
        const status = statusResponse.data || {};
        const runningItemIds = (status.items || [])
          .filter((item) => item.status === "running")
          .map((item) => String(item.map_id));

        setBulkRegenerationStatus(status);
        persistBulkRegenerationJob(jobId, status);
        setCurrentRegeneratingMapName(status.current_map_name || "");
        setRegeneratingMaps(new Set(runningItemIds));

        if (status.status === "completed" || status.status === "cancelled" || status.status === "failed") {
          stopBulkRegenerationPolling();
          clearPersistedBulkRegenerationJob();
          setIsRegenerating(false);
          setBulkRegenerationJobId(null);
          setCurrentRegeneratingMapName("");
          setRegeneratingMaps(new Set());
          setStopRegenerationRequested(false);
          stopRegenerationRequestedRef.current = false;
          await refreshMapsAfterBulkJob(mapIds || status.selected_map_ids || []);

          if (status.status === "failed") {
            alert(status.error || "Bulk regeneration failed.");
          } else if (status.status === "cancelled") {
            alert("Bulk regeneration stopped after the current map finished.");
          } else if ((status.failed_maps || 0) > 0) {
            alert(`Bulk regeneration finished with ${status.failed_maps} failure(s). Check the console for details.`);
          }
        }
      } catch (error) {
        console.error("Failed to poll bulk regeneration job:", error);
        stopBulkRegenerationPolling();
        setIsRegenerating(false);
        setCurrentRegeneratingMapName("");
        setRegeneratingMaps(new Set());
        setStopRegenerationRequested(false);
        stopRegenerationRequestedRef.current = false;
        alert(error?.response?.data?.detail || error?.message || "Failed to poll bulk regeneration job.");
      }
    };

    pollOnce();
    bulkRegenerationPollRef.current = window.setInterval(pollOnce, 5000);
  }, [
    clearPersistedBulkRegenerationJob,
    getApiHeaders,
    persistBulkRegenerationJob,
    refreshMapsAfterBulkJob,
    stopBulkRegenerationPolling,
  ]);

  const requestStopRegeneration = useCallback(async () => {
    if (!bulkRegenerationJobId) return;
    stopRegenerationRequestedRef.current = true;
    setStopRegenerationRequested(true);
    try {
      const headers = await getApiHeaders();
      await axios.post(
        buildApiUrl(`bulk_map_export_job/${bulkRegenerationJobId}/cancel/`),
        {},
        { headers }
      );
    } catch (error) {
      console.error("Failed to request bulk regeneration cancellation:", error);
      setStopRegenerationRequested(false);
      stopRegenerationRequestedRef.current = false;
      alert(error?.response?.data?.detail || error?.message || "Failed to request stop.");
    }
  }, [bulkRegenerationJobId, getApiHeaders]);

  const regenerateAllPDFs = useCallback(async () => {
    if (!canManageTierCapabilities || isRegenerating) return;
    if (effectiveBulkRegenerationCandidates.length === 0) {
      alert("No maps match the selected regeneration filters.");
      return;
    }

    const selectedMapIds = effectiveBulkRegenerationCandidates
      .map((mapItem) => String(mapItem?.id))
      .filter(Boolean);

    setIsRegenerating(true);
    setStopRegenerationRequested(false);
    stopRegenerationRequestedRef.current = false;
    setRegeneratingMaps(new Set());

    try {
      const headers = await getApiHeaders();
      const response = await axios.post(
        buildApiUrl("bulk_map_export/"),
        {
          map_ids: selectedMapIds,
          filters_snapshot: {
            scope: bulkRegenerationScope,
            ownership: bulkRegenerationOwnership,
            type: bulkRegenerationType,
            selected_map_ids: selectedBulkRegenerationMapIds,
          },
        },
        { headers }
      );
      const nextJobId = response?.data?.job_id;
      if (!nextJobId) {
        throw new Error("Bulk export job was not created");
      }
      setBulkRegenerationStatus(response?.data || null);
      setBulkRegenerationJobId(nextJobId);
      setShowBulkRegenerationControls(true);
      persistBulkRegenerationJob(nextJobId, response?.data || null);
      pollBulkRegenerationJob(nextJobId, selectedMapIds);
    } catch (error) {
      console.error("Failed to start bulk regeneration:", error);
      setIsRegenerating(false);
      setRegeneratingMaps(new Set());
      setStopRegenerationRequested(false);
      stopRegenerationRequestedRef.current = false;
      alert(error?.response?.data?.detail || error?.message || "Failed to start bulk regeneration.");
    }
  }, [
    bulkRegenerationOwnership,
    bulkRegenerationScope,
    bulkRegenerationType,
    canManageTierCapabilities,
    effectiveBulkRegenerationCandidates,
    getApiHeaders,
    isRegenerating,
    pollBulkRegenerationJob,
    persistBulkRegenerationJob,
    selectedBulkRegenerationMapIds,
  ]);

  useEffect(() => {
    if (!canManageTierCapabilities || bulkRegenerationJobId || typeof window === "undefined") {
      return;
    }
    let isMounted = true;

    const restoreBulkRegenerationJob = async () => {
      const persistedJob = loadPersistedBulkRegenerationJob();

      try {
        const headers = await getApiHeaders();
        const activeResponse = await axios.get(
          buildApiUrl("bulk_map_export_job/active/"),
          { headers }
        );
        const activeJob = activeResponse?.data?.job || null;
        if (!isMounted) return;

        if (activeJob?.job_id) {
          setShowBulkRegenerationControls(true);
          setBulkRegenerationJobId(activeJob.job_id);
          setBulkRegenerationStatus(activeJob);
          setCurrentRegeneratingMapName(activeJob.current_map_name || "");
          setIsRegenerating(true);
          setStopRegenerationRequested(activeJob.status === "cancel_requested");
          stopRegenerationRequestedRef.current = activeJob.status === "cancel_requested";
          persistBulkRegenerationJob(activeJob.job_id, activeJob);
          pollBulkRegenerationJob(activeJob.job_id, null);
          return;
        }
      } catch (error) {
        console.error("Failed to load active bulk regeneration job:", error);
      }

      if (!isMounted || !persistedJob?.jobId) {
        return;
      }

      setShowBulkRegenerationControls(true);
      setBulkRegenerationJobId(persistedJob.jobId);
      setBulkRegenerationStatus(persistedJob.status || null);
      setCurrentRegeneratingMapName(persistedJob.status?.current_map_name || "");
      const isTerminalStatus = ["completed", "failed", "cancelled"].includes(
        String(persistedJob.status?.status || "")
      );
      setIsRegenerating(!isTerminalStatus);
      setStopRegenerationRequested(persistedJob.status?.status === "cancel_requested");
      stopRegenerationRequestedRef.current = persistedJob.status?.status === "cancel_requested";
      if (!isTerminalStatus) {
        pollBulkRegenerationJob(persistedJob.jobId, null);
      }
    };

    restoreBulkRegenerationJob();

    return () => {
      isMounted = false;
    };
  }, [
    bulkRegenerationJobId,
    bulkRegenerationStorageKey,
    canManageTierCapabilities,
    getApiHeaders,
    loadPersistedBulkRegenerationJob,
    persistBulkRegenerationJob,
    pollBulkRegenerationJob,
  ]);

  // —— Cards data ——
  const cards = useMemo(() => {
    return filteredMaps
      .map((oneMap) => {
        const summary =
          (oneMap?.properties?.mappoints || [])
            .map((pt) => pt?.name)
            .filter(Boolean)
            .join(", ") || "";
        return {
          key: oneMap.id,
          header: replaceBrTags(oneMap?.properties?.area_name || ""),
          meta: replaceBrTags(oneMap?.properties?.country_name || ""),
          description: summary,
          is_corporate: !!oneMap?.is_corporate,
          is_versioned: !!oneMap?.is_versioned,
          revision_no: oneMap?.revision_no ?? null,
          can_edit: !!oneMap?.can_edit,
          is_visible_to_customers: oneMap?.properties?.is_visible_to_customers !== false,
          is_owned: String(oneMap?.properties?.map_user) === String(activeUser?.object_id),
          can_remove: !!oneMap?.is_versioned || String(oneMap?.properties?.map_user) === String(activeUser?.object_id),
          owner_tags: normalizeTags(oneMap?.properties?.owner_tags || []),
          my_tags: normalizeTags(oneMap?.properties?.my_tags || []),
          export_artifact_count: countMapExportArtifacts(oneMap?.properties || oneMap),
        };
      });
  }, [activeUser?.object_id, filteredMaps, normalizeTags]);

  const selectedMapAdditionalJpgArtifacts = useMemo(
    () => getAdditionalJpgArtifacts(selectedMapForQr?.properties || selectedMapForQr || {}),
    [selectedMapForQr]
  );

  return (
    <SidebarWrapper
      isMobile={isMobile}
      sidebarConfig={sidebarConfig}
      onClose={closeMapListBar}
      onHeightChange={onHeightChange}
      onToggleVisibility={(nextVisible) => {
        if (nextVisible === false) closeMapListBar();
      }}
      panelLabel="Maps"
      zIndex={500}
    >
      <Grid style={{ margin: "10px" }}>
        <Grid.Row centered>
          <Button.Group
            fluid
            style={{ marginBottom: "2px", marginLeft: "15px", marginRight: "15px" }}
          >
            <Button disabled>
              Maps
            </Button>
            {canManageTierCapabilities && (
              <Button
                icon
                onClick={() => setShowBulkRegenerationControls((prev) => !prev)}
                loading={isRegenerating}
                title="Bulk regenerate map exports"
              >
                <Icon name={showBulkRegenerationControls ? "close" : "refresh"} />
              </Button>
            )}
            <Button
              icon
              onClick={handleJoinMap}
              loading={isClaimingInvite}
              title="Join map via invite link"
            >
              <Icon name="linkify" />
            </Button>
          </Button.Group>
        </Grid.Row>

        <Grid.Row centered columns={2}>
          <Grid.Column width={12}>
            <Input
              icon="search"
              placeholder="Search maps..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ width: "100%", marginBottom: "2px" }}
            />
          </Grid.Column>
          <Grid.Column floated="right" width={2}>
            <Popup
              content="Create new map"
              trigger={<Button floated="right" icon="add" onClick={addMap} />}
            />
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={16}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", alignItems: "center" }}>
              <Dropdown
                fluid
                multiple
                search
                selection
                floating
                compact
                clearable
                placeholder="Owner tags"
                options={ownerTagOptions}
                value={selectedOwnerTags}
                style={{ minHeight: "36px" }}
                onChange={(event, data) => setSelectedOwnerTags(normalizeTags(data.value || []))}
              />
              <Dropdown
                fluid
                multiple
                search
                selection
                floating
                compact
                clearable
                placeholder="My tags"
                options={myTagOptions}
                value={selectedMyTags}
                style={{ minHeight: "36px" }}
                onChange={(event, data) => setSelectedMyTags(normalizeTags(data.value || []))}
              />
            </div>
          </Grid.Column>
        </Grid.Row>

        {canManageTierCapabilities && showBulkRegenerationControls && (
          <Grid.Row>
            <Grid.Column width={16}>
              <div
                style={{
                  border: "1px solid #dbe3ee",
                  borderRadius: "10px",
                  background: "#f8fbff",
                  padding: "12px",
                  display: "grid",
                  gap: "10px",
                }}
              >
                <div style={{ fontWeight: 700, color: "#23415f" }}>
                  Bulk Regeneration
                </div>
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: "8px" }}>
                  <Dropdown
                    fluid
                    selection
                    options={bulkRegenerationScopeOptions}
                    value={bulkRegenerationScope}
                    onChange={(event, data) => setBulkRegenerationScope(data.value)}
                    disabled={isRegenerating}
                  />
                  <Dropdown
                    fluid
                    selection
                    options={bulkRegenerationOwnershipOptions}
                    value={bulkRegenerationOwnership}
                    onChange={(event, data) => setBulkRegenerationOwnership(data.value)}
                    disabled={isRegenerating}
                  />
                  <Dropdown
                    fluid
                    selection
                    options={bulkRegenerationTypeOptions}
                    value={bulkRegenerationType}
                    onChange={(event, data) => setBulkRegenerationType(data.value)}
                    disabled={isRegenerating}
                  />
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                  <div style={{ fontSize: "12px", color: "#526277" }}>
                    Matching maps: <strong>{bulkRegenerationCandidates.length}</strong>
                    {selectedBulkRegenerationMapIds.length > 0
                      ? ` · selected for run: ${effectiveBulkRegenerationCandidates.length}`
                      : " · selected for run: all matching"}
                    {isRegenerating && currentRegeneratingMapName
                      ? ` · running: ${currentRegeneratingMapName}`
                      : ""}
                    {stopRegenerationRequested ? " · stop requested" : ""}
                  </div>
                  <div style={{ display: "flex", gap: "8px" }}>
                    {!isRegenerating && selectedBulkRegenerationMapIds.length > 0 && (
                      <Button
                        size="small"
                        basic
                        icon="erase"
                        content="Clear Selection"
                        onClick={clearBulkRegenerationSelection}
                      />
                    )}
                    {isRegenerating ? (
                      <Button
                        size="small"
                        color="orange"
                        icon="stop"
                        content="Stop After Current"
                        onClick={requestStopRegeneration}
                        disabled={stopRegenerationRequested}
                      />
                    ) : (
                      <Button
                        size="small"
                        color="blue"
                        icon="play"
                        content="Start"
                        onClick={regenerateAllPDFs}
                        disabled={effectiveBulkRegenerationCandidates.length === 0}
                      />
                    )}
                  </div>
                </div>
                {bulkRegenerationStatus && (
                  <div
                    style={{
                      borderTop: "1px solid #d7e1ec",
                      paddingTop: "10px",
                      display: "grid",
                      gap: "8px",
                    }}
                  >
                    <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", fontSize: "12px", color: "#35506b" }}>
                      <span><strong>Status:</strong> {bulkRegenerationStatus.status}</span>
                      <span><strong>Progress:</strong> {bulkRegenerationStatus.completed_maps || 0}/{bulkRegenerationStatus.total_maps || 0}</span>
                      <span><strong>Failed:</strong> {bulkRegenerationStatus.failed_maps || 0}</span>
                      {bulkRegenerationStatus.job_id && (
                        <span><strong>Job:</strong> {String(bulkRegenerationStatus.job_id).slice(0, 8)}</span>
                      )}
                    </div>
                    {bulkRegenerationStatus.message && (
                      <div style={{ fontSize: "12px", color: "#526277" }}>
                        {bulkRegenerationStatus.message}
                      </div>
                    )}
                    {bulkRegenerationStatus.summary && (
                      <div style={{ fontSize: "12px", color: "#526277" }}>
                        {bulkRegenerationStatus.summary}
                      </div>
                    )}
                    {bulkRegenerationStatus.error && (
                      <div style={{ fontSize: "12px", color: "#a33a3a" }}>
                        {bulkRegenerationStatus.error}
                      </div>
                    )}
                    {Array.isArray(bulkRegenerationStatus.items) && bulkRegenerationStatus.items.length > 0 && (
                      <div
                        style={{
                          display: "grid",
                          gap: "6px",
                          maxHeight: "132px",
                          overflowY: "auto",
                          paddingRight: "4px",
                        }}
                      >
                        {bulkRegenerationStatus.items.map((item) => {
                          const statusColor = item.status === "completed"
                            ? "#2e7d32"
                            : item.status === "failed"
                              ? "#c62828"
                              : item.status === "running"
                                ? "#1565c0"
                                : item.status === "cancelled"
                                  ? "#6d4c41"
                                  : "#607d8b";
                          return (
                            <div
                              key={item.id}
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                gap: "8px",
                                fontSize: "12px",
                                color: "#455a64",
                                background: "#ffffff",
                                border: "1px solid #dde6ef",
                                borderRadius: "8px",
                                padding: "6px 8px",
                              }}
                            >
                              <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {item.map_name}
                              </span>
                              <span style={{ color: statusColor, fontWeight: 600, whiteSpace: "nowrap" }}>
                                {item.status}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Grid.Column>
          </Grid.Row>
        )}

        {claimError && (
          <Grid.Row>
            <Grid.Column width={16}>
              <div style={{ color: "#b00020", fontSize: "12px" }}>{claimError}</div>
            </Grid.Column>
          </Grid.Row>
        )}

        <Grid.Row centered>
          <Card.Group
            centered
            style={{
              overflow: "auto",
              maxHeight: isMobile ? "30vh" : "80vh",
              overflowX: "hidden",
              width: "100%",
            }}
          >
            {cards.map((card) => {
              const cardKey = String(card.key);
              const isBulkSelectable = showBulkRegenerationControls && bulkRegenerationCandidateIdSet.has(cardKey);
              const isBulkSelected = selectedBulkRegenerationMapIds.some((mapId) => String(mapId) === cardKey);
              return (
              <Card
                centered
                key={card.key}
                onClick={() => isBulkSelectable && toggleBulkRegenerationSelection(card.key)}
                style={{
                  maxHeight: "140px",
                  margin: "10px",
                  width: "90%",
                  position: "relative",
                  cursor: isBulkSelectable ? "pointer" : "default",
                  border: isBulkSelected ? "2px solid #2185d0" : (isBulkSelectable ? "2px solid #dbe3ee" : undefined),
                  background: isBulkSelected ? "#edf6ff" : undefined,
                  boxShadow: isBulkSelected ? "0 0 0 3px rgba(33,133,208,0.15)" : undefined,
                }}
              >
                {regeneratingMaps.has(String(card.key)) && (
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      background: "rgba(255, 255, 255, 0.8)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 1,
                    }}
                  >
                    <Icon name="spinner" loading size="large" />
                  </div>
                )}
                <Card.Content textAlign="left">
                  <div style={{
                    float: "right",
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "4px",
                    width: "90px"
                  }}>
                    <Popup
                      content="View QR Code and Online Link"
                      trigger={
                        <div style={{ position: "relative", width: "100%", display: "flex" }}>
                          <Button
                            icon="qrcode"
                            onClick={(event) => {
                              event.stopPropagation();
                              openQrModal(
                                mapsToShow.find((m) => String(m.id) === String(card.key))
                              );
                            }}
                            style={{ padding: "8px", margin: 0, width: "100%" }}
                          />
                          {card.export_artifact_count > 0 && (
                            <div
                              style={{
                                position: "absolute",
                                left: "-5px",
                                bottom: "-5px",
                                minWidth: "16px",
                                height: "16px",
                                borderRadius: "8px",
                                background: "#2185d0",
                                color: "#ffffff",
                                fontSize: "10px",
                                lineHeight: "16px",
                                textAlign: "center",
                                fontWeight: 700,
                                padding: "0 4px",
                                boxShadow: "0 0 0 2px #ffffff",
                                pointerEvents: "none",
                              }}
                            >
                              {card.export_artifact_count}
                            </div>
                          )}
                        </div>
                      }
                    />
                    <Popup
                      content={card.can_edit ? "Edit this map" : "You don't have edit access"}
                      trigger={
                        <Button
                          onClick={(event) => {
                            event.stopPropagation();
                            if (card.can_edit) editMap(card.key);
                          }}
                          icon="edit"
                          disabled={!card.can_edit}
                          style={{ padding: "8px", margin: 0 }}
                        />
                      }
                    />
                    <Popup
                      content="Create your own editable version"
                      trigger={
                        <Button
                          icon="copy"
                          onClick={(event) => {
                            event.stopPropagation();
                            duplicateCorporateMap(
                              mapsToShow.find((m) => String(m.id) === String(card.key))
                            );
                          }}
                          style={{ padding: "8px", margin: 0 }}
                        />
                      }
                    />
                    <Popup
                      content={
                        card.is_owned
                          ? "Delete this map"
                          : card.can_remove
                            ? "Remove this map from your list"
                            : "Only owner maps can be deleted"
                      }
                      trigger={
                        <Button
                          icon="trash"
                          onClick={(event) => {
                            event.stopPropagation();
                            if (card.can_remove) deleteMap(card.key);
                          }}
                          disabled={!card.can_remove}
                          style={{ padding: "8px", margin: 0 }}
                        />
                      }
                    />
                  </div>
                  <Card.Header
                    style={{
                      color: card.is_visible_to_customers ? undefined : "#8a94a3",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      paddingRight: "96px",
                    }}
                  >
                    {!card.is_visible_to_customers ? (
                      <Popup
                        content="Hidden from travelers"
                        trigger={<Icon name="eye slash" style={{ color: "#8a94a3", margin: 0, flex: "0 0 auto" }} />}
                      />
                    ) : null}
                    <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {card.header}
                    </span>
                  </Card.Header>
                  <Card.Meta>
                    {card.meta}
                    {card.is_versioned && (
                      <span style={{ marginLeft: "8px", color: "#666" }}>
                        {card.revision_no ? `Rev ${card.revision_no}` : "Versioned"}
                      </span>
                    )}
                  </Card.Meta>
                  {card.owner_tags?.length > 0 && (
                    <Card.Description style={{ fontSize: "11px", marginTop: "4px", color: "#444" }}>
                      Owner tags: {card.owner_tags.join(", ")}
                    </Card.Description>
                  )}
                  {card.my_tags?.length > 0 && (
                    <Card.Description style={{ fontSize: "11px", color: "#666" }}>
                      My tags: {card.my_tags.join(", ")}
                    </Card.Description>
                  )}
                  {/* Optional summary text: */}
                  {/* <Card.Description>{card.description}</Card.Description> */}
                </Card.Content>
              </Card>
              );
            })}
          </Card.Group>
        </Grid.Row>
      </Grid>

      {/* QR Code Modal */}
      <Modal
        open={qrModalOpen}
        onClose={closeQrModal}
        size="small"
        centered={false}
      >
        <Modal.Header>
          {selectedMapForQr?.area_name || selectedMapForQr?.properties?.area_name} - QR Code & Online Link
        </Modal.Header>
        <Modal.Content>
          {selectedMapForQr && (
            <div style={{ textAlign: "center" }}>
              <div style={{ marginBottom: "20px" }}>
                <h3>{selectedMapForQr.area_name || selectedMapForQr.properties?.area_name}</h3>
                <p>{selectedMapForQr.country_name || selectedMapForQr.properties?.country_name}</p>
              </div>
              
              {/* QR Code Image */}
              {(selectedMapForQr.api_key || selectedMapForQr.properties?.api_key) && (
                <div style={{ marginBottom: "20px" }}>
                  <h4>QR Code:</h4>
                  <img
                    src={buildMapsUrl(`exported_maps/qrcodes/${selectedMapForQr.api_key || selectedMapForQr.properties?.api_key}.png`)}
                    alt="QR Code"
                    style={{
                      maxWidth: "200px",
                      maxHeight: "200px",
                      border: "1px solid #ddd",
                      borderRadius: "8px",
                    }}
                  />
                </div>
              )}
              
              {/* Online Link */}
              {(selectedMapForQr.api_key || selectedMapForQr.properties?.api_key) && (
                <div style={{ marginBottom: "20px" }}>
                  <h4>Online Map Link:</h4>
                  <div style={{ 
                    padding: "10px", 
                    backgroundColor: "#f5f5f5", 
                    borderRadius: "4px",
                    wordBreak: "break-all"
                  }}>
                    <a 
                      href={`https://qrmaps.me/${selectedMapForQr.api_key || selectedMapForQr.properties?.api_key}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#0066cc", textDecoration: "none" }}
                    >
                      https://qrmaps.me/{selectedMapForQr.api_key || selectedMapForQr.properties?.api_key}
                    </a>
                  </div>
                  <Button
                    primary
                    style={{ marginTop: "10px" }}
                    onClick={() => window.open(`https://qrmaps.me/${selectedMapForQr.api_key || selectedMapForQr.properties?.api_key}`, '_blank')}
                  >
                    Open Map Online
                  </Button>
                </div>
              )}
              
              {/* Copy Link Button */}
              {(selectedMapForQr.api_key || selectedMapForQr.properties?.api_key) && (
                <Button
                  secondary
                  icon="copy"
                  content="Copy Link"
                  onClick={() => {
                    navigator.clipboard.writeText(`https://qrmaps.me/${selectedMapForQr.api_key || selectedMapForQr.properties?.api_key}`);
                    // You could add a toast notification here if you have one
                  }}
                />
              )}
              
              {/* PDF Downloads Section */}
              {(
                selectedMapForQr.properties?.pdf_path ||
                selectedMapForQr.properties?.pdf_path_no_qr ||
                selectedMapForQr.properties?.pdf_vector_path ||
                selectedMapForQr.properties?.pdf_vector_path_no_qr ||
                selectedMapForQr.properties?.jpg_125dpi_q85_path ||
                selectedMapForQr.properties?.jpg_125dpi_q85_path_no_qr ||
                selectedMapForQr.properties?.jpg_200dpi_q90_path ||
                selectedMapForQr.properties?.jpg_200dpi_q90_path_no_qr ||
                selectedMapAdditionalJpgArtifacts.length > 0
              ) && (
                <div style={{ marginTop: "30px", borderTop: "1px solid #ddd", paddingTop: "20px" }}>
                  <h4 style={{ marginBottom: "15px" }}>Download Map Files:</h4>
                  <div style={{ 
                    display: "flex", 
                    justifyContent: "center", 
                    gap: "20px",
                    flexWrap: "wrap"
                  }}>
                    {selectedMapForQr.properties?.pdf_path && (
                      renderDownloadCard({
                        href: buildMapsUrl(selectedMapForQr.properties.pdf_path),
                        icon: "file pdf",
                        label: "Raster PDF with QR",
                        borderColor: "#d32f2f",
                        hoverColor: "#fdecec",
                        badgeIcon: "qrcode",
                      })
                    )}
                    {selectedMapForQr.properties?.pdf_path_no_qr && (
                      renderDownloadCard({
                        href: buildMapsUrl(selectedMapForQr.properties.pdf_path_no_qr),
                        icon: "file pdf outline",
                        label: "Raster PDF no QR",
                        borderColor: "#6c757d",
                        hoverColor: "#e9ecef",
                      })
                    )}
                    {selectedMapForQr.properties?.pdf_vector_path && (
                      renderDownloadCard({
                        href: buildMapsUrl(selectedMapForQr.properties.pdf_vector_path),
                        icon: "file pdf",
                        label: "Vector PDF with QR",
                        borderColor: "#8e24aa",
                        hoverColor: "#f3e5f5",
                        badgeIcon: "qrcode",
                      })
                    )}
                    {selectedMapForQr.properties?.pdf_vector_path_no_qr && (
                      renderDownloadCard({
                        href: buildMapsUrl(selectedMapForQr.properties.pdf_vector_path_no_qr),
                        icon: "file pdf outline",
                        label: "Vector PDF no QR",
                        borderColor: "#5e35b1",
                        hoverColor: "#ede7f6",
                      })
                    )}
                    {selectedMapForQr.properties?.jpg_125dpi_q85_path && (
                      renderDownloadCard({
                        href: buildMapsUrl(selectedMapForQr.properties.jpg_125dpi_q85_path),
                        icon: "file image",
                        label: "JPG 125 DPI with QR",
                        borderColor: "#1e88e5",
                        hoverColor: "#e3f2fd",
                        badgeIcon: "qrcode",
                      })
                    )}
                    {selectedMapForQr.properties?.jpg_125dpi_q85_path_no_qr && (
                      renderDownloadCard({
                        href: buildMapsUrl(selectedMapForQr.properties.jpg_125dpi_q85_path_no_qr),
                        icon: "file image outline",
                        label: "JPG 125 DPI no QR",
                        borderColor: "#1565c0",
                        hoverColor: "#e3f2fd",
                      })
                    )}
                    {selectedMapForQr.properties?.jpg_200dpi_q90_path && (
                      renderDownloadCard({
                        href: buildMapsUrl(selectedMapForQr.properties.jpg_200dpi_q90_path),
                        icon: "file image",
                        label: "JPG 200 DPI with QR",
                        borderColor: "#00897b",
                        hoverColor: "#e0f2f1",
                        badgeIcon: "qrcode",
                      })
                    )}
                    {selectedMapForQr.properties?.jpg_200dpi_q90_path_no_qr && (
                      renderDownloadCard({
                        href: buildMapsUrl(selectedMapForQr.properties.jpg_200dpi_q90_path_no_qr),
                        icon: "file image outline",
                        label: "JPG 200 DPI no QR",
                        borderColor: "#00695c",
                        hoverColor: "#e0f2f1",
                      })
                    )}
                    {selectedMapAdditionalJpgArtifacts.map((artifact) => (
                      <React.Fragment key={artifact.key}>
                        {renderDownloadCard({
                          href: buildMapsUrl(artifact.path),
                          icon: artifact.variant === "with_qr" ? "file image" : "file image outline",
                          label: artifact.label,
                          borderColor: "#3949ab",
                          hoverColor: "#e8eaf6",
                          badgeIcon: artifact.variant === "with_qr" ? "qrcode" : null,
                        })}
                      </React.Fragment>
                    ))}
                  </div>
                  {!selectedMapForQr.properties?.pdf_vector_path
                    && !selectedMapForQr.properties?.pdf_vector_path_no_qr
                    && selectedMapForQr.properties?.pdf_vector_unavailable_reason && (
                      <div style={{ marginTop: "14px", fontSize: "13px", color: "#6b4f00" }}>
                        Vector PDF unavailable: {selectedMapForQr.properties.pdf_vector_unavailable_reason}
                      </div>
                    )}
                </div>
              )}
            </div>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={closeQrModal}>Close</Button>
        </Modal.Actions>
      </Modal>
    </SidebarWrapper>
  );
}
